<script>
import App from './App'
import { mapState } from 'pinia'
import { useSettingsStore } from 'Store/settings'
import { availableLanguages, ChatChannels, LocalStorageKeys } from 'javascript/Constants'
import { useAppStore } from 'Store/app'
import { useIconMessageStore } from 'Store/icon-message'
import { checkIsFullScreen } from 'Utilities'
import { addListenerForRouteChangesInSPA } from 'Utilities/browserHistory'
import { useColorsStore } from 'Store/colors'
import { useChatStore } from 'Store/chat'
import { useBotIconStore } from 'Store/bot-icon'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { useBrandChatbotStore } from 'Store/brand-chatbot'
import { useRestaurantStore } from 'Store/restaurant'
import { Setting, User, Chat } from 'src/apis'
import { useTermsConditionsStore } from 'Store/terms-conditions'
import { useMaintenanceStore } from 'Store/maintenance'
import callAnonymousSignIn from 'Utilities/anonymousSignIn'
import Vue from 'vue'
import Gtag from 'javascript/Gtag'
import Cookies from 'js-cookie'
import { storeSession } from 'Utilities/Session'
import { setTier } from 'Utilities/tier-manager'
import { useCookieConsentStore } from 'Store/cookie-consent'
import { EventBus } from 'ChatWidget/EventBus'
import { loadLineLiffSDK } from 'javascript/helpers/liff'

export default {
  name: 'Root',

  components: {
    App
  },

  props: {
    scriptConfig: { type: Object, required: true },
    triplabotSettings: { type: Object, required: true }
  },

  setup() {
    return {
      appStore: useAppStore(),
      settingsStore: useSettingsStore(),
      iconMessageStore: useIconMessageStore(),
      colorsStore: useColorsStore(),
      botIconStore: useBotIconStore(),
      brandChatbotStore: useBrandChatbotStore(),
      restaurantStore: useRestaurantStore(),
      termsConditionsStore: useTermsConditionsStore(),
      maintenanceStore: useMaintenanceStore(),
      cookieConsentStore: useCookieConsentStore(),
      chatStore: useChatStore()
    }
  },

  data() {
    return {
      show: false,
      lineToken: '',
      redirectUrl: '',
      lineProfile: {},
      uid: ''
    }
  },

  computed: {
    isFullScreen() {
      return this.appStore.isFullScreen || this.settingsStore.isCustomBotTestMode
    },
    ...mapState(useSettingsStore, {
      isWhitelisted: 'isCurrentLocationWhitelisted',
      settings: 'getSettings'
    })
  },

  watch: {
    isWhitelisted: {
      handler(value) {
        if (value) {
          this.appendIconMessages()
        } else {
          this.appStore.resetIframeHaveLoadedCSS()
          this.iconMessageStore.clearIconMessages()
          this.iconMessageStore.resetClosedIconMessage()
        }
      },
      immediate: true
    }
  },

  created() {
    this.setupGtag()
    this.manipulateStoreData()
    this.manipulateMaintenanceData()

    const session = Cookies.get(`triplabot_session_${this.settingsStore.settings?.hotel_id}`)
    if (session) {
      storeSession(session.replace(/\|\d+$/, ''))
    }

    this.setupListernersForSPA()
    this.setupBrandChatbot()
    this.setupTermsAndConditions()
    this.setupChatChannel()

    // make sure we call this method after store data has been manipulated
    // coz it accesses some data in store
    this.doAnonymousSignIn()

    window.addEventListener('message', this.handleMessageFromOtherWidgets)
  },

  methods: {
    appendIconMessages() {
      availableLanguages.map((language) => {
        this.iconMessageStore.appendIconMessages({
          language,
          messages: this.settings[language]?.icon?.icon_message || []
        })
      })
    },

    async doAnonymousSignIn() {
      let lineToken = null
      try {
        if (this.settings.line_liff_setting?.liff_enabled) {
          await loadLineLiffSDK()
          const data = await this.initLiffAndProfile()
          lineToken = data?.lineToken
        }

        await callAnonymousSignIn(this.triplabotSettings, lineToken)

        // when user trigger chatbot by LINE APP will not display chatbot Icon
        this.show = !window.liff?.isInClient()
        EventBus.$emit('chat-widget-ready')
      } catch (error) {
        console.error('Error in LIFF initialization or anonymous sign-in:', error)
      }
    },

    async initLiffAndProfile() {
      const initSettings = this.triplabotSettings
      const isLIFFEnabled = initSettings.line_liff_setting?.liff_enabled
      if (!window.liff || !isLIFFEnabled || this.settingsStore.isOnStandaloneBookingWidget) return

      try {
        await window.liff.init({ liffId: initSettings.line_liff_setting.liff_app_id })

        if (window.liff.isLoggedIn() && window.liff.isInClient()) {
          // Get data from Liff
          const queryString = window.location.search
          const urlParams = new URLSearchParams(queryString)
          this.redirectUrl = urlParams.get('redirect_url')
          this.uid = this.redirectUrl.match(/[?&]uid=([^&]+)/)?.[1] || urlParams.get('uid')
          const profile = await window.liff.getProfile()
          this.lineProfile = JSON.stringify(profile)
          this.lineToken = window.liff.getIDToken()

          sessionStorage.setItem('line_liff_redirect_url', this.redirectUrl)
          sessionStorage.setItem('line_liff_profile', this.lineProfile)
          sessionStorage.setItem('line_liff_token', this.lineToken)
          sessionStorage.setItem('line_liff_anonymous_uid', this.uid)

          if (this.uid) {
            const finallUid = this.uid
            const lineToken =
              this.lineToken ||
              sessionStorage.getItem('line_liff_token') ||
              window.liff.getIDToken()
            User.ConnectLineLiffUser(finallUid, lineToken)
          }
          window.postMessage('liff-ready', '*')
        }
      } catch (error) {
        console.error('Error during LIFF initialization:', error)
        throw error
      }

      return { lineToken: this.lineToken }
    },

    manipulateStoreData() {
      this.settingsStore.updateInitialSettings(this.triplabotSettings)
      setTier(this.triplabotSettings.tier)
      this.settingsStore.setScriptDataAttributes(this.scriptConfig.dataAttrs)
      this.appStore.setWebsiteFacilityCode(this.scriptConfig.code)
      this.appStore.setWindowLocation(window.location.toString())
      this.appStore.setBaseURL(this.scriptConfig.base_url)
      this.appStore.setIsFullScreen(checkIsFullScreen() || this.settingsStore.isCustomBotTestMode)
      this.restaurantStore.setCode(this.scriptConfig.dataAttrs['data-triplabot-restaurant-code'])
      this.colorsStore.setThemeAndTextColor({
        themeColor: this.settings.theme_color,
        textColor: this.settings.font_color
      })
      this.botIconStore.updateIconSize({
        mobile: this.settings.icon_size_mobile,
        pc: this.settings.icon_size_pc
      })
      this.iconMessageStore.updatePosition(this.settings.icon_message_position)
    },

    manipulateMaintenanceData() {
      if (!this.triplabotSettings.maintenance) return

      this.maintenanceStore.updateErrorTitle(json.overlay_title[this.$i18n.locale])
      this.maintenanceStore.updateErrorBody(json.overlay_message[this.$i18n.locale])
      this.maintenanceStore.updateMode(true)
      this.maintenanceStore.updateSearchBarOn(json.hotel_config.search_bar_is_active)
      this.maintenanceStore.updateBookingWidgetOn(json.hotel_config.booking_widget_is_active)
    },

    setupGtag() {
      const gtag = new Gtag(this.triplabotSettings)
      Vue.prototype.$gtag = gtag

      if (
        !this.settingsStore.isCookieConsentEnabled ||
        this.cookieConsentStore.isPerformanceCookieEnabled
      ) {
        gtag.enableTracking()
      }
    },

    setupTermsAndConditions() {
      if (!this.triplabotSettings.terms_and_conditions_enabled) return

      Setting.getTermsConditions(this.appStore.websiteFacilityCode).then((res) => {
        this.termsConditionsStore.setSettings(res.data)
      })
    },

    setupListernersForSPA() {
      addListenerForRouteChangesInSPA(() => {
        this.appStore.setWindowLocation(window.location.toString())
      })

      // for back button on browsers
      window.addEventListener('popstate', () => {
        this.appStore.setWindowLocation(window.location.toString())
      })
    },

    setupBrandChatbot() {
      // Handle the case where we have previously switched a
      // chat, but only if we are on the branch hotel. If we
      // are on the branch directly, we want to suppress the
      // back button, so add a flag to state too.
      if (
        triplaLocalStorage.getItem('original_brand_hotel_code') &&
        triplaLocalStorage.getItem('branch_chat_switched_to')
      ) {
        if (this.scriptConfig.code === triplaLocalStorage.getItem('original_brand_hotel_code')) {
          this.scriptConfig.code = triplaLocalStorage.getItem('branch_chat_switched_to')
          this.scriptConfig.hide_back_button = false
          this.brandChatbotStore.setIsBrand(false)
        } else {
          this.brandChatbotStore.setIsBrand(true)
        }
      }
    },

    setupChatChannel() {
      if (this.settingsStore.isCustomBotTestMode) {
        this.appStore.setChatChannel(ChatChannels.WEB)
        return
      }

      if (this.settingsStore.availableChatChannels.length === 1) {
        this.appStore.setChatChannel(this.settingsStore.availableChatChannels[0])
        triplaLocalStorage.setItem(
          LocalStorageKeys.SELECTED_CHAT_CHANNEL,
          this.settingsStore.availableChatChannels[0],
          true
        )
      } else if (!this.settingsStore.availableChatChannels.includes(this.appStore.chatChannel)) {
        triplaLocalStorage.removeItem(LocalStorageKeys.SELECTED_CHAT_CHANNEL)
      }
    },
    handleMessageFromOtherWidgets(event) {
      const bwURL = new URL(this.settingsStore.bookingWidgetURL)
      const guideURL = new URL(this.settingsStore.getSettings.guide_url)
      if (
        event.origin !== bwURL.origin &&
        event.origin !== process.env.FAQ_WIDGET_URL &&
        event.origin !== guideURL.origin
      )
        return

      // handle from recommender widget & faq widget
      const { data } = event
      if (data.action === 'ga4Event') {
        this.$gtag.trackGA4Event(data.event, data.payload)
      } else if (data.action === 'guide-send-message') {
        // If chatbot is not open, open it first
        if (!document.querySelector('.tripla-chat-window--show')) {
          window.triplaToggleChat()
        }
        if (this.chatStore.chatId) {
          this.sendMessageToChat(data.payload)
        } else {
          // If chatbot is not initialized, wait for it to be initialized
          const loadChatsHandler = () => {
            this.sendMessageToChat(data.payload)
            EventBus.$off('load-chats', loadChatsHandler)
          }
          EventBus.$on('load-chats', loadChatsHandler)
        }
      }
    },
    sendMessageToChat(payload) {
      const { message, ...rest } = payload
      Chat.sendMessage(message, { ...rest, resetValue: true })
    }
  },

  render(h) {
    const isActive = this.settingsStore.getSettings.active || this.settingsStore.isCustomBotTestMode
    if (!isActive || !this.isWhitelisted || !this.show) return undefined

    return h(App)
  }
}
</script>
